import {
    Cable,
    FileText,
    Handshake,
    LayoutDashboard,
    Shield,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { LocalRoutes } from "@/LocalRoutes";
import { Roles } from "@/auth/user-roles";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserRoles } from "@/hooks/use-user-roles";
import { useSidebar } from "@/components/ui/sidebar-context";
import { cn } from "@/lib/utils";

const items = [
    {
        titleKey: "routes.dashboard",
        url: LocalRoutes.DASHBOARD,
        icon: LayoutDashboard,
    },
    {
        titleKey: "routes.metering-points",
        url: LocalRoutes.METERING_POINTS,
        icon: Cable,
    },
    {
        titleKey: "routes.member-contracts",
        url: LocalRoutes.MEMBER_CONTRACTS,
        icon: Handshake,
    },
    {
        titleKey: "routes.data-exports",
        url: LocalRoutes.DATA_EXPORTS,
        icon: FileText,
    },
] as const;

export const MobileBottomNav = () => {
    const isMobile = useIsMobile();
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const { hasAnyRole } = useUserRoles();
    const { setOpenMobile } = useSidebar();

    if (!isMobile) {
        return null;
    }

    const isAdmin = hasAnyRole(Roles.ADMIN, Roles.ROOT, Roles.SUPER_ROOT_3000);

    return (
        <nav
            aria-label={t("common.primary-navigation")}
            className={cn(
                "fixed z-30 flex bg-sidebar border-sidebar-border",
                "portrait:inset-x-0 portrait:bottom-0 portrait:h-16 portrait:flex-row portrait:border-t portrait:pb-[env(safe-area-inset-bottom)] portrait:shadow-[0_-1px_2px_rgba(0,0,0,0.04)]",
                "landscape:inset-y-0 landscape:left-0 landscape:w-16 landscape:flex-col landscape:border-r landscape:pl-[env(safe-area-inset-left)] landscape:shadow-[1px_0_2px_rgba(0,0,0,0.04)]",
            )}
        >
            {items.map((item) => {
                const isActive = pathname === item.url;
                const Icon = item.icon;
                return (
                    <Link
                        aria-current={isActive ? "page" : undefined}
                        className={cn(
                            "flex flex-1 flex-col items-center justify-center gap-1 px-2 py-1 text-xs transition-colors",
                            isActive
                                ? "text-sidebar-accent-foreground"
                                : "text-sidebar-foreground/70 hover:text-sidebar-foreground",
                        )}
                        key={item.url}
                        to={item.url}
                    >
                        <Icon
                            className={cn(
                                "size-5 transition-transform",
                                isActive && "scale-110",
                            )}
                        />
                        <span
                            className={cn(
                                "truncate text-[11px] leading-none",
                                isActive && "font-medium",
                            )}
                        >
                            {t(item.titleKey)}
                        </span>
                    </Link>
                );
            })}
            {isAdmin ? (
                <button
                    aria-label={t("routes.admin")}
                    className={cn(
                        "flex flex-1 flex-col items-center justify-center gap-1 px-2 py-1 text-xs transition-colors",
                        "text-sidebar-foreground/70 hover:text-sidebar-foreground",
                    )}
                    onClick={() => setOpenMobile(true)}
                    type="button"
                >
                    <Shield className="size-5 transition-transform" />
                    <span className="truncate text-[11px] leading-none">
                        {t("routes.admin")}
                    </span>
                </button>
            ) : null}
        </nav>
    );
};
